import { combineActions, handleActions } from 'redux-actions';
import {
    DELETE_BANK_ACCOUNT_FAIL,
    DELETE_BANK_ACCOUNT_FAIL_ACTION,
    DELETE_BANK_ACCOUNT_REQUEST,
    DELETE_BANK_ACCOUNT_REQUEST_ACTION,
    DELETE_BANK_ACCOUNT_SUCCESS,
    DELETE_BANK_ACCOUNT_SUCCESS_ACTION,
    FETCH_PLAID_TEMPORARY_TOKEN_FAIL,
    FETCH_PLAID_TEMPORARY_TOKEN_FAIL_ACTION,
    FETCH_PLAID_TEMPORARY_TOKEN_REQUEST,
    FETCH_PLAID_TEMPORARY_TOKEN_REQUEST_ACTION,
    FETCH_PLAID_TEMPORARY_TOKEN_SUCCESS,
    FETCH_PLAID_TEMPORARY_TOKEN_SUCCESS_ACTION,
    LOAD_BANK_ACCOUNTS_FAIL,
    LOAD_BANK_ACCOUNTS_FAIL_ACTION,
    LOAD_BANK_ACCOUNTS_REQUEST,
    LOAD_BANK_ACCOUNTS_REQUEST_ACTION,
    LOAD_BANK_ACCOUNTS_SUCCESS,
    LOAD_BANK_ACCOUNTS_SUCCESS_ACTION,
    POST_BANK_ACCOUNT_FAIL,
    POST_BANK_ACCOUNT_FAIL_ACTION,
    POST_BANK_ACCOUNT_REQUEST,
    POST_BANK_ACCOUNT_REQUEST_ACTION,
    POST_BANK_ACCOUNT_SUCCESS,
    POST_BANK_ACCOUNT_SUCCESS_ACTION,
    SET_PLAID_PUBLIC_TOKEN_FAIL,
    SET_PLAID_PUBLIC_TOKEN_FAIL_ACTION,
    SET_PLAID_PUBLIC_TOKEN_REQUEST,
    SET_PLAID_PUBLIC_TOKEN_REQUEST_ACTION,
    SET_PLAID_PUBLIC_TOKEN_SUCCESS,
    SET_PLAID_PUBLIC_TOKEN_SUCCESS_ACTION,
} from './actions';
import {
    deleteBankAccount,
    fetchBankAccounts,
    fetchPlaidTemporaryToken,
    postAddBankAccount,
    setPlaidPublicToken,
} from '@/redux/api/payment';
import { getAuthToken, getBidderId } from './user';
import { BankAccount } from '@liveauctioneers/caterwaul-components/types/BankAccount';
import { createSelector } from 'reselect';
import { getDeployment } from './config';
import { GlobalState } from '../rootReducer';
import paymentProviders from '../../enums/paymentProviders';

export type State = {
    readonly bankAccounts: BankAccount[];
    readonly bankAccountsLoaded?: Date;
    readonly bankAccountsLoading: boolean;
    readonly errorMessage: string | undefined;
    plaidError: boolean;
    plaidFetching: boolean;
    plaidSuccess: boolean;
    plaidTemporaryToken: string;
};

export const DEFAULT_STATE: State = {
    bankAccounts: [],
    bankAccountsLoaded: undefined,
    bankAccountsLoading: false,
    errorMessage: undefined,
    plaidError: false,
    plaidFetching: false,
    plaidSuccess: false,
    plaidTemporaryToken: '',
};

export const bankAccount = handleActions(
    {
        [combineActions(DELETE_BANK_ACCOUNT_FAIL, LOAD_BANK_ACCOUNTS_FAIL, POST_BANK_ACCOUNT_FAIL)]: (
            state: State,
            action: DELETE_BANK_ACCOUNT_FAIL_ACTION | LOAD_BANK_ACCOUNTS_FAIL_ACTION | POST_BANK_ACCOUNT_FAIL_ACTION
        ): State => {
            return {
                ...state,
                bankAccountsLoading: false,
                errorMessage: action.payload,
            };
        },
        [combineActions(DELETE_BANK_ACCOUNT_REQUEST, LOAD_BANK_ACCOUNTS_REQUEST)]: (state: State): State => {
            return {
                ...state,
                bankAccountsLoaded: undefined,
                bankAccountsLoading: true,
                errorMessage: undefined,
            };
        },
        [combineActions(FETCH_PLAID_TEMPORARY_TOKEN_FAIL, SET_PLAID_PUBLIC_TOKEN_FAIL)]: (state: State): State => {
            return {
                ...state,
                plaidError: true,
                plaidFetching: false,
                plaidSuccess: false,
            };
        },
        [combineActions(FETCH_PLAID_TEMPORARY_TOKEN_REQUEST, SET_PLAID_PUBLIC_TOKEN_REQUEST)]: (
            state: State
        ): State => {
            return {
                ...state,
                plaidError: false,
                plaidFetching: true,
                plaidSuccess: false,
            };
        },
        [DELETE_BANK_ACCOUNT_SUCCESS]: (state: State): State => {
            return {
                ...state,
                bankAccounts: [],
                errorMessage: undefined,
            };
        },
        [FETCH_PLAID_TEMPORARY_TOKEN_SUCCESS]: (
            state: State,
            action: FETCH_PLAID_TEMPORARY_TOKEN_SUCCESS_ACTION
        ): State => ({
            ...state,
            plaidError: false,
            plaidFetching: false,
            plaidSuccess: true,
            plaidTemporaryToken: action.payload,
        }),
        [LOAD_BANK_ACCOUNTS_SUCCESS]: (state: State, action: LOAD_BANK_ACCOUNTS_SUCCESS_ACTION): State => {
            return {
                ...state,
                bankAccounts: action.payload || [],
                bankAccountsLoaded: new Date(),
                bankAccountsLoading: false,
                errorMessage: undefined,
            };
        },
        [POST_BANK_ACCOUNT_SUCCESS]: (state: State, action: POST_BANK_ACCOUNT_SUCCESS_ACTION): State => {
            return {
                ...state,
                bankAccounts: [...state.bankAccounts, action.payload],
                errorMessage: undefined,
            };
        },
        [SET_PLAID_PUBLIC_TOKEN_SUCCESS]: (state: State, action: SET_PLAID_PUBLIC_TOKEN_SUCCESS_ACTION): State => ({
            ...state,
            bankAccounts: [...state.bankAccounts, action.payload],
            plaidError: false,
            plaidFetching: false,
            plaidSuccess: true,
        }),
    },
    DEFAULT_STATE
);

const stateSelector = (state: GlobalState) => state.bankAccount;
const passthroughSelector = (state: GlobalState, id) => id;

export const getBankAccounts = createSelector(stateSelector, (state) => state.bankAccounts);
export const getBankAccountsLoading = createSelector(stateSelector, (state) => state.bankAccountsLoading);
export const getBankAccountsLoaded = createSelector(stateSelector, (state) => state.bankAccountsLoaded);

export const defaultBankAccountSelector = createSelector(
    [getBankAccounts, passthroughSelector],
    (bankAccounts, providerId) =>
        bankAccounts?.find((bankAccount) => bankAccount.defaultAccount && bankAccount.providerId === providerId)
);

export const getUiBankAccount: (state: GlobalState) => State = createSelector(stateSelector, (state) => state);

export const getPlaid = createSelector(stateSelector, (state) => state);

export const getPlaidTemporaryToken = createSelector(getPlaid, (plaidState) => plaidState.plaidTemporaryToken);

const shouldFetchBankAccounts = createSelector(stateSelector, (state) => {
    return !state.bankAccountsLoading && Array.isArray(state.bankAccounts) && state.bankAccounts.length === 0;
});

export const fetchBankAccountsIfNeeded = () => async (dispatch: Function, getState: () => GlobalState) => {
    const state = getState();

    if (!shouldFetchBankAccounts(state)) {
        return Promise.resolve({});
    }

    try {
        const authToken = getAuthToken(state);
        const bidderId = getBidderId(state);
        const deployment = getDeployment(state);

        dispatch({
            type: LOAD_BANK_ACCOUNTS_REQUEST,
        } as LOAD_BANK_ACCOUNTS_REQUEST_ACTION);

        const response = await fetchBankAccounts({ authToken, bidderId, deployment });
        return dispatch({
            payload: response.payload,
            type: LOAD_BANK_ACCOUNTS_SUCCESS,
        } as LOAD_BANK_ACCOUNTS_SUCCESS_ACTION);
    } catch (error) {
        dispatch({
            error: true,
            payload: error,
            type: LOAD_BANK_ACCOUNTS_FAIL,
        } as LOAD_BANK_ACCOUNTS_FAIL_ACTION);
    }
};

type SubmitPostBankAccountParams = {
    accountNumber: string;
    bankAccountName: string;
    firstName: string;
    lastName: string;
    providerId: number;
    routingNumber: string;
};

export const submitPostBankAccount =
    ({ accountNumber, bankAccountName, firstName, lastName, routingNumber }: SubmitPostBankAccountParams) =>
    async (dispatch: Function, getState: () => GlobalState) => {
        try {
            const state = getState();
            const authToken = getAuthToken(state);
            const bidderId = getBidderId(state);
            const deployment = getDeployment(state);

            dispatch({
                type: POST_BANK_ACCOUNT_REQUEST,
            } as POST_BANK_ACCOUNT_REQUEST_ACTION);

            const response = await postAddBankAccount({
                accountNumber,
                authToken,
                bankAccountName,
                bidderId,
                deployment,
                firstName,
                lastName,
                providerId: paymentProviders.PAYRIX,
                routingNumber,
            });
            dispatch({
                payload: response.payload,
                type: POST_BANK_ACCOUNT_SUCCESS,
            } as POST_BANK_ACCOUNT_SUCCESS_ACTION);
        } catch (error) {
            dispatch({
                error: true,
                payload: error,
                type: POST_BANK_ACCOUNT_FAIL,
            } as POST_BANK_ACCOUNT_FAIL_ACTION);
            throw error;
        }
    };

export const submitDeleteBankAccount = (bankAccountId: number) => async (dispatch: Function, getState: Function) => {
    try {
        const state = getState();
        const authToken = getAuthToken(state);
        const bidderId = getBidderId(state);
        const deployment = getDeployment(state);

        dispatch({
            type: DELETE_BANK_ACCOUNT_REQUEST,
        } as DELETE_BANK_ACCOUNT_REQUEST_ACTION);

        await deleteBankAccount({ authToken, bankAccountId, bidderId, deployment });

        dispatch({ type: DELETE_BANK_ACCOUNT_SUCCESS } as DELETE_BANK_ACCOUNT_SUCCESS_ACTION);

        dispatch(fetchBankAccountsIfNeeded());
    } catch (error) {
        dispatch({
            error: true,
            payload: error,
            type: DELETE_BANK_ACCOUNT_FAIL,
        } as DELETE_BANK_ACCOUNT_FAIL_ACTION);
    }
};

export const fetchBidderTemporaryPlaidToken =
    (bidderId: number) => async (dispatch: Function, getState: () => GlobalState) => {
        try {
            const state = getState();
            const authToken = getAuthToken(state);
            const deployment = getDeployment(state);

            dispatch({
                type: FETCH_PLAID_TEMPORARY_TOKEN_REQUEST,
            } as FETCH_PLAID_TEMPORARY_TOKEN_REQUEST_ACTION);

            const response = await fetchPlaidTemporaryToken({ authToken, bidderId, deployment });

            dispatch({
                payload: response.payload,
                type: FETCH_PLAID_TEMPORARY_TOKEN_SUCCESS,
            } as FETCH_PLAID_TEMPORARY_TOKEN_SUCCESS_ACTION);
        } catch (error) {
            dispatch({
                type: FETCH_PLAID_TEMPORARY_TOKEN_FAIL,
            } as FETCH_PLAID_TEMPORARY_TOKEN_FAIL_ACTION);
        }
    };

export const setBidderPlaidPublicToken =
    (bidderId: number, token: string, accountID: string, firstName: string, lastName: string) =>
    async (dispatch: Function, getState: () => GlobalState) => {
        try {
            const state = getState();
            const authToken = getAuthToken(state);
            const deployment = getDeployment(state);

            dispatch({
                type: SET_PLAID_PUBLIC_TOKEN_REQUEST,
            } as SET_PLAID_PUBLIC_TOKEN_REQUEST_ACTION);

            const response = await setPlaidPublicToken({
                accountID,
                authToken,
                bidderId,
                deployment,
                firstName,
                lastName,
                token,
            });

            dispatch({
                payload: response.payload as BankAccount,
                type: SET_PLAID_PUBLIC_TOKEN_SUCCESS,
            } as SET_PLAID_PUBLIC_TOKEN_SUCCESS_ACTION);
        } catch (error) {
            dispatch({
                type: SET_PLAID_PUBLIC_TOKEN_FAIL,
            } as SET_PLAID_PUBLIC_TOKEN_FAIL_ACTION);
        }
    };
